
import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";

import Index from '@/pages/Index';
import Auth from '@/pages/Auth';
import NotFound from '@/pages/NotFound';
import Dashboard from '@/pages/Dashboard';
import JournalistProfile from '@/pages/JournalistProfile';
import ArticleReader from '@/pages/ArticleReader';
import ReaderHome from '@/pages/ReaderHome';
import Settings from '@/pages/Settings';
import JournalistOnboarding from '@/pages/JournalistOnboarding';
import ReaderOnboarding from '@/pages/ReaderOnboarding';
import TrendingTopics from '@/pages/TrendingTopics';
import ArticlePreviewPage from '@/pages/ArticlePreviewPage';
import Profile from '@/pages/Profile';
import Subscription from '@/pages/Subscription';
import Help from '@/pages/Help';
import AllAuthors from '@/pages/AllAuthors';
import TaggedArticlesView from '@/components/reader-home/TaggedArticlesView';
import HeaderWithAuth from '@/components/layout/HeaderWithAuth';
import AIAssistant from '@/components/AIAssistant';

import ProtectedRoute from '@/components/auth/ProtectedRoute';

// Layout component that includes the header for authenticated routes
const AuthenticatedLayout = () => (
  <HeaderWithAuth>
    <Outlet />
  </HeaderWithAuth>
);

// Create a ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Component to render global app elements
const AppWithRouter = ({ router }) => {
  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
      <SonnerToaster />
    </>
  );
};

// Component that includes the AIAssistant - used outside the Router context
const AppWithAIAssistant = () => {
  return (
    <>
      <AppWithRouter router={router} />
      <AIAssistant />
    </>
  );
};

const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollToTop />
        <Outlet />
      </>
    ),
    children: [
      {
        path: '/',
        element: <Index />,
      },
      {
        path: '/auth',
        element: <Auth />,
      },
      // Dashboard already includes its own header
      {
        path: '/dashboard/*',
        element: (
          <ProtectedRoute requiredRoles={['journalist', 'admin']}>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      // Routes that need the authenticated header
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: '/journalist/:userId',
            element: <JournalistProfile />,
          },
          {
            path: '/article/:articleId',
            element: <ArticleReader />,
          },
          {
            path: '/article/:articleId/preview',
            element: (
              <ProtectedRoute requiredRoles={['journalist', 'admin']}>
                <ArticlePreviewPage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/home',
            element: (
              <ProtectedRoute>
                <ReaderHome />
              </ProtectedRoute>
            ),
          },
          {
            path: '/settings',
            element: (
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            ),
          },
          {
            path: '/journalist-onboarding',
            element: (
              <ProtectedRoute requiredRoles={['journalist']}>
                <JournalistOnboarding />
              </ProtectedRoute>
            ),
          },
          {
            path: '/reader-onboarding',
            element: (
              <ProtectedRoute>
                <ReaderOnboarding />
              </ProtectedRoute>
            ),
          },
          {
            path: '/trending',
            element: <TrendingTopics />,
          },
          {
            path: '/tags/:tagName',
            element: <TaggedArticlesView />,
          },
          {
            path: '/profile',
            element: (
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            ),
          },
          {
            path: '/subscription',
            element: (
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            ),
          },
          {
            path: '/authors',
            element: <AllAuthors />,
          },
          {
            path: '/help',
            element: <Help />,
          },
        ]
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ]
  }
]);

export default function Routes() {
  return <AppWithAIAssistant />;
}
